const { get } = require('lodash');

/* eslint-disable camelcase */
const personalizedTypes = {
  hero: 'HERO',
  square: {
    large: 'SQUARE_LARGE',
    small: 'SQUARE_SMALL'
  },
  vertical: 'VERTICAL'
};

/**
 * remapLink - This function remaps link data in the Personalized HP Tile into the shape a regular
 * Tile requires.
 *
 * @param {object} persoLink - The Personalized HP Tile componentData to be modified
 * @returns {object} - Returns the coordinates object which will be used a regular Tile's CopySet
 */
const remapLink = function remapLink(persoLink) {
  return {
    AddNewLine: persoLink.PlaceThisLinkOnNewLine,
    DestinationServer: persoLink.LinkDestinationServer,
    Text: persoLink.LinkText,
    Title: persoLink.LinkHoverText,
    Target: persoLink.LinkWindowTarget,
    LinkURL: persoLink.LinkURL,
    LinkStyle: persoLink.LinkStyle
  };
};

/**
 * remapCoordinates - This function chooses and remaps the coordintate data in the
 * Personalized HP Tile into the shape a regular Tile requires. The type determines
 * how the copy is presented
 *
 * @param {object} persoData - The Personalized HP Tile componentData to be modified
 * @param {string} type - The type of Tile it will be transformed to
 * @returns {object} - Returns the coordinates object which will be used a regular Tile's CopySet
 */
const remapCoordinates = function remapCoordinates(persoData, type) {
  let typeKey;
  let coordinatesKey;
  const coordinates = {};

  switch (type) {
    case personalizedTypes.vertical:
      typeKey = 'Vertical';
      coordinatesKey = 'VerticalDesktopCoordinates';
      break;
    case personalizedTypes.hero:
    default:
      typeKey = 'Hero';
      coordinates.MobileDisplayForCopySet = 'Off Image';
  }

  if (type !== personalizedTypes.square.small) {
    if (persoData[typeKey][coordinatesKey]) {
      coordinates.LiveTextX = persoData[typeKey][coordinatesKey].CopySetX;
      coordinates.LiveTextY = persoData[typeKey][coordinatesKey].CopySetY;
      coordinates.LiveTextWidth = persoData[typeKey][coordinatesKey].CopySetWidth;
    }
  }
  return coordinates;
};

/**
 * remapCopySet - This function chooses and remaps the copy data in the Personalized HP Tile
 * into the shape a regular Tile requires. The type determines how the copy is presented
 *
 * @param {object} persoData - The Personalized HP Tile componentData to be modified
 * @param {string} type - The type of Tile it will be transformed to
 * @param {boolean} isMobile - isMobile, defaults to true
 * @returns {object} - Returns the ImageLink object used in a regular Tile
 */
const remapCopySet = function remapCopySet(persoData, type, isMobile) {
  const persoCopySet = persoData.CopySet;
  const copySet = [];

  if (persoCopySet) {
    copySet.push(remapCoordinates(persoData, type));
    const newCopySet = copySet[0];

    // remap all links
    if (Array.isArray(persoCopySet.Links)) {
      newCopySet.Links = persoCopySet.Links.map(remapLink);
    }

    // live text
    newCopySet.LiveText = persoCopySet.LiveText;

    // schema doesn't support plain text headlines
    if (persoCopySet.HeadlineLiveText) {
      newCopySet.Heading = [
        {
          HeadlineLiveText: persoCopySet.HeadlineLiveText
        }
      ];
    }

    switch (type) {
      case personalizedTypes.vertical:
        newCopySet.TextColor = persoData.Vertical.VerticalDesktopTextColor;
        break;
      case personalizedTypes.hero:
      default:
        newCopySet.TextColor = isMobile
          ? get(persoData, 'Hero.HeroMobileTextColor', '')
          : get(persoData, 'Hero.HeroDesktopTextColor', '');
    }

    if (persoCopySet.LinkHeading) {
      newCopySet.LinkHeading = persoCopySet.LinkHeading;
    }
  }

  return copySet;
};

/**
 * remapImage - This function chooses and remaps the image data in the Personalized HP Tile
 * into the shape a regular Tile requires. The type specified determines which image will
 * be used (tall vertical or square hero).
 *
 * @param {object} persoData - The Personalized HP Tile componentData to be modified
 * @param {string} type - The type of Tile it will be transformed to
 * @returns {object} - Returns the ImageLink object used in a regular Tile
 */
const remapImage = function remapImage(persoData, type) {
  const imageLink = {
    Image_Url: {
      ImageUrl: '',
      AltText: ''
    },
    MobileImage_Url: {
      ImageUrl: '',
      AltText: ''
    },
    DestinationServer: 'Relative',
    Target: 'Default',
    LinkURL: '',
    Title: ''
  };

  // if the mobile asset isn't present, use the desktop image
  switch (type) {
    case personalizedTypes.vertical:
      imageLink.Image_Url.ImageUrl = persoData.Vertical.VerticalDesktopImage
        ? persoData.Vertical.VerticalDesktopImage.ImageUrl
        : undefined;
      imageLink.MobileImage_Url.ImageUrl = persoData.Vertical.VerticalMobileImage
        ? persoData.Vertical.VerticalMobileImage.ImageUrl
        : persoData.Vertical.VerticalDesktopImage.ImageUrl;
      break;
    case personalizedTypes.hero:
    default:
      imageLink.MobileImage_Url.ImageUrl = persoData.Hero.HeroMobileImage
        ? persoData.Hero.HeroMobileImage.ImageUrl
        : persoData.Hero.HeroDesktopImage.ImageUrl;
  }

  imageLink.Image_Url.AltText = persoData.ImageAltText;
  imageLink.Title = persoData.ImageHoverText;
  imageLink.LinkURL = persoData.ImageLinkURL;
  imageLink.MobileImage_Url.AltText = persoData.ImageAltText;

  return imageLink;
};

/**
 * phpTileMapper - This function changes a Personalized HP Tile into a regular Tile. The type
 * specified determines which image will be used (tall vertical or square hero) or how copy
 * will be presented.
 *
 * @param {object} persoData - The Personalized HP Tile componentData to be modified
 * @param {string} type - The type of Tile it will be transformed to
 * @param {boolean} isMobile - isMobile, defaults to true
 * @returns {object} - Returns a regular Tile
 */
const phpTileMapper = function phpTileMapper(persoData, type, isMobile = true) {
  const clone = {
    ComponentName: persoData.ComponentName,
    Id: persoData.Id,
    SchemaName: 'Tile'
  };

  clone.ImageLink = remapImage(persoData, type);
  clone.CopySet = remapCopySet(persoData, type, isMobile);

  return clone;
};

module.exports = {
  personalizedTypes,
  phpTileMapper
};
